import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { API, formatCurrency } from '../lib/utils';
import { 
  UNIT_OPTIONS, 
  getUnitLabel, 
  getPrestationLabel, 
  getDomainLabel,
  getCategoryLabel, 
  getCategoryGroup, 
  TVA_RATES 
} from '../config/serviceCategories';
import { toast } from 'sonner';
import { 
  Plus, Trash2, ChevronLeft, Save, FileText, 
  User, Package, Percent, Euro, Search, Check, Edit, Info,
  PenLine, Send, CheckCircle
} from 'lucide-react';

const QuoteBuilderPage = ({ isEditMode = false }) => {
  const navigate = useNavigate();
  const { id: quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const { company, token } = useAuth();
  const preselectedClientId = searchParams.get('client');
  
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [originalQuote, setOriginalQuote] = useState(null);
  
  // Post-creation modal state
  const [showRecapModal, setShowRecapModal] = useState(false);
  const [createdQuote, setCreatedQuote] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Quote data
  const [selectedClientId, setSelectedClientId] = useState(preselectedClientId || '');
  const [lines, setLines] = useState([]);
  const [discountType, setDiscountType] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('Paiement à réception de facture');
  const [validityDays, setValidityDays] = useState(30);
  // New fields
  const [referenceChantier, setReferenceChantier] = useState('');
  const [delaiIntervention, setDelaiIntervention] = useState('');
  const [siteAddress, setSiteAddress] = useState('');

  // New line form
  const [newLine, setNewLine] = useState({
    description: '',
    quantity: '1',
    unit: 'hour',
    unit_price_ht: '',
    tva_rate: 20,
    service_id: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Pre-fill payment terms from company profile if available and not editing
  useEffect(() => {
    if (!isEditMode && company?.default_payment_delay && paymentTerms === 'Paiement à réception de facture') {
      const delayMapping = {
        'reception': 'Paiement à réception de facture',
        '30': 'Paiement sous 30 jours',
        '45': 'Paiement sous 45 jours',
        '60': 'Paiement sous 60 jours'
      };
      const mappedTerms = delayMapping[company.default_payment_delay];
      if (mappedTerms) {
        setPaymentTerms(mappedTerms);
      }
    }
  }, [company, isEditMode, paymentTerms]);

  const fetchData = async () => {
    try {
      const [clientsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/services`)
      ]);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
      
      // If edit mode, load existing quote
      if (isEditMode && quoteId) {
        const quoteRes = await axios.get(`${API}/quotes/${quoteId}`);
        const quote = quoteRes.data;
        setOriginalQuote(quote);
        setSelectedClientId(quote.client_id);
        setLines(quote.lines.map(ln => ({
          id: ln.id || Date.now() + Math.random(),
          description: ln.description,
          quantity: ln.quantity,
          unit: ln.unit,
          unit_price_ht: ln.unit_price_ht,
          tva_rate: ln.tva_rate,
          service_id: ln.service_id
        })));
        setDiscountType(quote.discount_type || '');
        setDiscountValue(quote.discount_value?.toString() || '');
        setNotes(quote.notes || '');
        setPaymentTerms(quote.payment_terms || 'Paiement à réception de facture');
        setReferenceChantier(quote.reference_chantier || '');
        setDelaiIntervention(quote.delai_intervention || '');
        setSiteAddress(quote.site_address || '');
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      if (isEditMode) navigate('/quotes');
    }
    setLoading(false);
  };

  // Check if company uses TVA franchise
  const isFranchise = company?.tva_regime === 'franchise';

  const addLineFromService = (service) => {
    // Use the prestation/category name as the quote line description
    const prestationLabel = getPrestationLabel(service.category, service.domain) || service.description;
    
    const line = {
      id: Date.now(),
      description: prestationLabel,
      quantity: 1,
      unit: service.unit,
      unit_price_ht: service.unit_price_ht,
      tva_rate: isFranchise ? 0 : service.tva_rate,
      service_id: service.id
    };
    setLines([...lines, line]);
    setServiceDialogOpen(false);
    setCatalogSearch('');
    toast.success(`✓ ${prestationLabel} ajouté`);
  };

  // Filter and group services for catalog modal
  const filteredServices = useMemo(() => {
    if (!catalogSearch.trim()) return services;
    const search = catalogSearch.toLowerCase();
    return services.filter(s => {
      const prestationLabel = getPrestationLabel(s.category, s.domain) || s.description;
      const domainLabel = getDomainLabel(s.domain) || '';
      return prestationLabel.toLowerCase().includes(search) ||
             domainLabel.toLowerCase().includes(search) ||
             s.description?.toLowerCase().includes(search);
    });
  }, [services, catalogSearch]);

  const groupedCatalogServices = useMemo(() => {
    const groups = {};
    filteredServices.forEach(service => {
      const groupName = service.domain 
        ? getDomainLabel(service.domain).toUpperCase()
        : getCategoryGroup(service.category);
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(service);
    });
    return groups;
  }, [filteredServices]);

  const addCustomLine = () => {
    if (!newLine.description || !newLine.unit_price_ht) {
      toast.error('Veuillez remplir la description et le prix');
      return;
    }
    const line = {
      id: Date.now(),
      description: newLine.description,
      quantity: parseFloat(newLine.quantity) || 1,
      unit: newLine.unit,
      unit_price_ht: parseFloat(newLine.unit_price_ht),
      tva_rate: isFranchise ? 0 : newLine.tva_rate,
      service_id: null
    };
    setLines([...lines, line]);
    setNewLine({
      description: '',
      quantity: '1',
      unit: 'hour',
      unit_price_ht: '',
      tva_rate: isFranchise ? 0 : 20,
      service_id: null
    });
    toast.success('Ligne ajoutée');
  };

  const updateLine = (lineId, field, value) => {
    setLines(lines.map(line => 
      line.id === lineId ? { ...line, [field]: value } : line
    ));
  };

  const removeLine = (lineId) => {
    setLines(lines.filter(line => line.id !== lineId));
  };

  // Calculate totals
  const calculateLineTotals = (line) => {
    const total_ht = line.quantity * line.unit_price_ht;
    const total_tva = total_ht * (line.tva_rate / 100);
    return { total_ht, total_tva, total_ttc: total_ht + total_tva };
  };

  const subtotalHT = lines.reduce((sum, line) => sum + (line.quantity * line.unit_price_ht), 0);
  const subtotalTVA = lines.reduce((sum, line) => {
    return sum + (line.quantity * line.unit_price_ht * (line.tva_rate / 100));
  }, 0);

  let discount = 0;
  if (discountType === 'percent' && discountValue) {
    discount = subtotalHT * (parseFloat(discountValue) / 100);
  } else if (discountType === 'fixed' && discountValue) {
    discount = parseFloat(discountValue);
  }

  const totalHT = subtotalHT - discount;
  const totalTVA = subtotalHT > 0 ? subtotalTVA * (1 - discount / subtotalHT) : 0;
  const totalTTC = totalHT + totalTVA;

  const handleSave = async () => {
    if (!selectedClientId) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    if (lines.length === 0) {
      toast.error('Ajoutez au moins une ligne au devis');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        client_id: selectedClientId,
        lines: lines.map(line => ({
          description: line.description,
          quantity: parseFloat(line.quantity),
          unit: line.unit,
          unit_price_ht: parseFloat(line.unit_price_ht),
          tva_rate: parseFloat(line.tva_rate),
          service_id: line.service_id
        })),
        discount_type: discountType || null,
        discount_value: discountValue ? parseFloat(discountValue) : 0,
        notes: notes || null,
        payment_terms: paymentTerms,
        validity_days: validityDays,
        reference_chantier: referenceChantier || null,
        delai_intervention: delaiIntervention || null,
        site_address: siteAddress || null
      };

      if (isEditMode && quoteId) {
        await axios.put(`${API}/quotes/${quoteId}`, payload);
        toast.success('Devis modifié avec succès !');
        navigate(`/quotes/${quoteId}`);
      } else {
        const response = await axios.post(`${API}/quotes`, payload);
        // Get the selected client info
        const selectedClient = clients.find(c => c.id === selectedClientId);
        // Show recap modal instead of navigating directly
        setCreatedQuote({
          ...response.data,
          client: selectedClient
        });
        setShowRecapModal(true);
        toast.success('Devis créé avec succès !');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  // Handle "Sign Now" action
  const handleSignNow = () => {
    if (createdQuote) {
      navigate(`/quotes/${createdQuote.id}?action=sign`);
    }
  };

  // Handle "Send by Email" action
  const handleSendEmail = async () => {
    if (!createdQuote) return;
    const clientEmail = createdQuote.client?.email;
    if (!clientEmail) {
      toast.error("Email du client non renseigné");
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(
        `${API}/quotes/${createdQuote.id}/request-signature`,
        { recipient_email: clientEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email envoyé à ${clientEmail}`);
      setShowRecapModal(false);
      navigate(`/quotes/${createdQuote.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    }
    setSendingEmail(false);
  };

  // Navigate to quote detail
  const handleViewQuote = () => {
    if (createdQuote) {
      navigate(`/quotes/${createdQuote.id}`);
    }
  };

  if (loading) {
    return (
      <div className="page-container p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-48 bg-muted rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container pb-48 md:pb-8 p-4 md:p-8 noise-overlay">
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase">
            {isEditMode ? `Modifier ${originalQuote?.quote_number || 'devis'}` : 'Nouveau devis'}
          </h1>
        </div>

        {/* TVA Franchise Banner */}
        {isFranchise && (
          <div className="flex items-center gap-3 p-4 mb-6 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <p className="text-sm text-blue-400">
              Franchise en base de TVA - TVA non applicable. Tous les montants sont hors taxe.
            </p>
          </div>
        )}

        {/* Step 1: Select Client */}
        <Card className="bg-card border-white/10 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-primary" />
              1. Sélectionner le client
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="select-client">
                <SelectValue placeholder="Choisir un client..." />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name} {client.company && `(${client.company})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {clients.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                <Button variant="link" className="p-0 h-auto text-primary" onClick={() => navigate('/clients')}>
                  Créez d'abord un client
                </Button>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Add Lines */}
        <Card className="bg-card border-white/10 mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-primary" />
                2. Ajouter des lignes
              </CardTitle>
              <Dialog open={serviceDialogOpen} onOpenChange={(open) => { setServiceDialogOpen(open); if (!open) setCatalogSearch(''); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-white/10" data-testid="add-from-catalog-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Du catalogue
                  </Button>
                </DialogTrigger>
                <DialogContent 
                  onPointerDownOutside={(e) => e.preventDefault()}
                  onInteractOutside={(e) => e.preventDefault()}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  className="bg-card border-white/10 max-w-xl max-h-[85vh] flex flex-col p-0">
                  <DialogHeader className="p-6 pb-0">
                    <DialogTitle className="text-xl font-bold uppercase">Ajouter depuis le catalogue</DialogTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Cliquez sur un service pour l'ajouter instantanément
                    </p>
                  </DialogHeader>
                  
                  {/* Search bar */}
                  <div className="px-6 py-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        placeholder="Rechercher un service..."
                        value={catalogSearch}
                        onChange={(e) => setCatalogSearch(e.target.value)}
                        className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary"
                        data-testid="catalog-search-input"
                        autoFocus={false}
                      />
                    </div>
                  </div>
                  
                  {/* Services list grouped by category */}
                  <div className="flex-1 overflow-y-auto px-6 pb-6">
                    {Object.keys(groupedCatalogServices).length > 0 ? (
                      <div className="space-y-4">
                        {Object.entries(groupedCatalogServices).map(([groupName, groupServices]) => (
                          <div key={groupName}>
                            <h3 className="text-xs font-bold uppercase tracking-wider text-primary mb-2 sticky top-0 bg-card py-1">
                              {groupName} ({groupServices.length})
                            </h3>
                            <div className="space-y-2">
                              {groupServices.map(service => {
                                const prestationLabel = getPrestationLabel(service.category, service.domain) || service.description;
                                const domainLabel = getDomainLabel(service.domain) || getCategoryGroup(service.category);
                                return (
                                  <div
                                    key={service.id}
                                    onClick={() => addLineFromService(service)}
                                    className="bg-muted/30 hover:bg-primary/20 border border-transparent hover:border-primary/50 rounded-lg p-4 cursor-pointer transition-all group"
                                    data-testid={`catalog-service-${service.id}`}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex-1 min-w-0">
                                        {/* Main title: Prestation */}
                                        <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                                          {prestationLabel}
                                        </p>
                                        {/* Subtitle: Métier */}
                                        <p className="text-sm text-muted-foreground mt-0.5">
                                          {domainLabel}
                                        </p>
                                      </div>
                                      <div className="text-right flex-shrink-0">
                                        <p className="font-bold text-primary">
                                          {formatCurrency(service.unit_price_ht)}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          / {getUnitLabel(service.unit)} • TVA {service.tva_rate}%
                                        </p>
                                      </div>
                                    </div>
                                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Check className="w-3 h-3 text-primary" />
                                    <span>Cliquer pour ajouter</span>
                                  </div>
                                </div>
                              );
                            })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">
                          {catalogSearch ? 'Aucun service trouvé' : 'Aucun service dans le catalogue'}
                        </p>
                        {catalogSearch && (
                          <Button 
                            variant="link" 
                            onClick={() => setCatalogSearch('')}
                            className="text-primary mt-2"
                          >
                            Effacer la recherche
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Existing Lines */}
            {lines.length > 0 && (
              <div className="space-y-2 mb-4">
                {lines.map((line, index) => {
                  const totals = calculateLineTotals(line);
                  return (
                    <div key={line.id} className="bg-muted/30 rounded-lg p-3" data-testid={`quote-line-${index}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="font-medium flex-1">{line.description}</p>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLine(line.id)}
                          className="h-8 w-8 text-destructive hover:bg-destructive/20 flex-shrink-0"
                          data-testid={`remove-line-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className={`grid ${line.unit === 'forfait' ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3 md:grid-cols-5'} gap-2 text-sm`}>
                        {line.unit !== 'forfait' && (
                          <div>
                            <Label className="text-xs text-muted-foreground">Qté</Label>
                            <Input
                              type="number"
                              value={line.quantity}
                              onChange={(e) => updateLine(line.id, 'quantity', parseFloat(e.target.value) || 0)}
                              className="h-9 bg-muted/50 border-transparent text-center"
                            />
                          </div>
                        )}
                        <div>
                          <Label className="text-xs text-muted-foreground">Unité</Label>
                          <Select value={line.unit} onValueChange={(v) => {
                            updateLine(line.id, 'unit', v);
                            if (v === 'forfait') updateLine(line.id, 'quantity', 1);
                          }}>
                            <SelectTrigger className="h-9 bg-muted/50 border-transparent">
                              <SelectValue>{getUnitLabel(line.unit)}</SelectValue>
                            </SelectTrigger>
                            <SelectContent className="bg-card border-white/10">
                              {UNIT_OPTIONS.filter(u => !u.isCustom).map(u => (
                                <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">{line.unit === 'forfait' ? 'Montant HT' : 'Prix HT'}</Label>
                          <Input
                            type="number"
                            value={line.unit_price_ht}
                            onChange={(e) => updateLine(line.id, 'unit_price_ht', parseFloat(e.target.value) || 0)}
                            className="h-9 bg-muted/50 border-transparent text-right"
                          />
                        </div>
                        {!isFranchise && (
                          <div className="hidden md:block">
                            <Label className="text-xs text-muted-foreground">TVA</Label>
                            <Select value={line.tva_rate.toString()} onValueChange={(v) => updateLine(line.id, 'tva_rate', parseFloat(v))}>
                              <SelectTrigger className="h-9 bg-muted/50 border-transparent">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-white/10">
                                {TVA_RATES.map(rate => (
                                  <SelectItem key={rate.value} value={rate.value.toString()}>{rate.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="hidden md:block">
                          <Label className="text-xs text-muted-foreground">{isFranchise ? 'Total' : 'Total TTC'}</Label>
                          <p className="h-9 flex items-center justify-end font-semibold text-primary">
                            {formatCurrency(isFranchise ? totals.total_ht : totals.total_ttc)}
                          </p>
                        </div>
                      </div>
                      <p className="text-right text-sm font-semibold text-primary md:hidden mt-2">
                        {formatCurrency(isFranchise ? totals.total_ht : totals.total_ttc)} {isFranchise ? '' : 'TTC'}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Custom Line */}
            <div className="border border-dashed border-white/20 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-3">Ajouter une ligne personnalisée</p>
              <div className="space-y-3">
                <Input
                  placeholder="Description de la prestation..."
                  value={newLine.description}
                  onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
                  className="h-12 bg-muted/50 border-transparent focus:border-primary"
                  data-testid="new-line-description"
                />
                <div className={`grid ${newLine.unit === 'forfait' ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4'} gap-3`}>
                  {newLine.unit !== 'forfait' && (
                    <Input
                      type="number"
                      placeholder="Qté"
                      value={newLine.quantity}
                      onChange={(e) => setNewLine({ ...newLine, quantity: e.target.value })}
                      className="h-12 bg-muted/50 border-transparent"
                      data-testid="new-line-quantity"
                    />
                  )}
                  <Select value={newLine.unit} onValueChange={(v) => setNewLine({ ...newLine, unit: v, quantity: v === 'forfait' ? '1' : newLine.quantity })}>
                    <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="new-line-unit">
                      <SelectValue>{getUnitLabel(newLine.unit)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      {UNIT_OPTIONS.filter(u => !u.isCustom).map(u => (
                        <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder={newLine.unit === 'forfait' ? 'Montant forfaitaire HT' : 'Prix HT'}
                    value={newLine.unit_price_ht}
                    onChange={(e) => setNewLine({ ...newLine, unit_price_ht: e.target.value })}
                    className="h-12 bg-muted/50 border-transparent"
                    data-testid="new-line-price"
                  />
                  {!isFranchise && (
                    <Select value={newLine.tva_rate.toString()} onValueChange={(v) => setNewLine({ ...newLine, tva_rate: parseFloat(v) })}>
                      <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="new-line-tva">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-white/10">
                        {TVA_RATES.map(rate => (
                          <SelectItem key={rate.value} value={rate.value.toString()}>TVA {rate.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={addCustomLine}
                  className="w-full h-12 border-white/10"
                  data-testid="add-custom-line-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter cette ligne
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Options */}
        <Card className="bg-card border-white/10 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-primary" />
              3. Options
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Discount */}
            <div>
              <Label className="text-sm text-muted-foreground">Remise</Label>
              <div className="flex gap-2 mt-1">
                <Select value={discountType} onValueChange={setDiscountType}>
                  <SelectTrigger className="w-32 h-12 bg-muted/50 border-transparent" data-testid="discount-type-select">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="none">Aucune</SelectItem>
                    <SelectItem value="percent">%</SelectItem>
                    <SelectItem value="fixed">€</SelectItem>
                  </SelectContent>
                </Select>
                {discountType && discountType !== 'none' && (
                  <Input
                    type="number"
                    placeholder={discountType === 'percent' ? '10' : '50'}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="flex-1 h-12 bg-muted/50 border-transparent"
                    data-testid="discount-value-input"
                  />
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes / Conditions particulières</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 bg-muted/50 border-transparent focus:border-primary min-h-[80px]"
                placeholder="Conditions particulières, détails..."
                data-testid="quote-notes"
              />
            </div>

            {/* Payment Terms */}
            <div>
              <Label htmlFor="payment_terms">Conditions de paiement</Label>
              <Input
                id="payment_terms"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className="mt-1 h-12 bg-muted/50 border-transparent focus:border-primary"
                data-testid="payment-terms"
              />
            </div>

            {/* Validity */}
            <div>
              <Label htmlFor="validity">Validité du devis (jours)</Label>
              <Input
                id="validity"
                type="number"
                value={validityDays}
                onChange={(e) => setValidityDays(parseInt(e.target.value) || 30)}
                className="mt-1 h-12 bg-muted/50 border-transparent focus:border-primary w-32"
                data-testid="validity-days"
              />
            </div>

            {/* Adresse du chantier */}
            <div>
              <Label htmlFor="site_address">Adresse du chantier (optionnel)</Label>
              <Input
                id="site_address"
                value={siteAddress}
                onChange={(e) => setSiteAddress(e.target.value)}
                className="mt-1 h-12 bg-muted/50 border-transparent focus:border-primary"
                placeholder="Ex: 12 rue des Lilas, 31000 Toulouse"
                data-testid="site-address"
              />
            </div>

            {/* Reference Chantier */}
            <div>
              <Label htmlFor="reference_chantier">Référence chantier (optionnel)</Label>
              <Input
                id="reference_chantier"
                value={referenceChantier}
                onChange={(e) => setReferenceChantier(e.target.value)}
                className="mt-1 h-12 bg-muted/50 border-transparent focus:border-primary"
                placeholder="Ex: Rénovation salle de bain"
                data-testid="reference-chantier"
              />
            </div>

            {/* Delai Intervention */}
            <div>
              <Label htmlFor="delai_intervention">Délai d'intervention (optionnel)</Label>
              <Input
                id="delai_intervention"
                value={delaiIntervention}
                onChange={(e) => setDelaiIntervention(e.target.value)}
                className="mt-1 h-12 bg-muted/50 border-transparent focus:border-primary"
                placeholder="Ex: Intervention sous 48h"
                data-testid="delai-intervention"
              />
            </div>
          </CardContent>
        </Card>

        {/* Create Button - No totals during drafting (per UX decision) */}
        <Card className="bg-card border-white/10 mb-6">
          <CardContent className="p-4 md:p-6">
            {lines.length === 0 && (
              <p className="text-sm text-muted-foreground text-center mb-4">
                Ajoutez au moins une ligne de prestation pour créer le devis
              </p>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || !selectedClientId || lines.length === 0}
              className={`w-full h-12 font-bold uppercase tracking-wide rounded-full ${
                lines.length === 0 
                  ? 'bg-muted text-muted-foreground cursor-not-allowed' 
                  : 'bg-primary text-primary-foreground hover:bg-accent'
              }`}
              data-testid="save-quote-btn"
            >
              {saving ? 'Enregistrement...' : (isEditMode ? 'Enregistrer les modifications' : 'Créer le devis')}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Post-creation Recap Modal */}
      <Dialog open={showRecapModal} onOpenChange={setShowRecapModal}>
        <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="quote-recap-modal">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold">Votre devis est prêt !</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  {createdQuote?.quote_number}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {createdQuote && (
            <div className="space-y-4">
              {/* Recap Summary */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{createdQuote.client?.name || createdQuote.client_name}</span>
                </div>
                
                <div className="border-t border-white/10 pt-3">
                  <p className="text-xs text-muted-foreground mb-2">Prestations ({createdQuote.lines?.length || 0})</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {createdQuote.lines?.slice(0, 5).map((line, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="truncate mr-2">{line.description}</span>
                        <span className="text-muted-foreground whitespace-nowrap">{formatCurrency(line.total_ht)}</span>
                      </div>
                    ))}
                    {createdQuote.lines?.length > 5 && (
                      <p className="text-xs text-muted-foreground">+ {createdQuote.lines.length - 5} autres lignes</p>
                    )}
                  </div>
                </div>

                <div className="border-t border-white/10 pt-3 space-y-1">
                  {(() => {
                    const subtotalHT = createdQuote.lines?.reduce((sum, l) => sum + (l.total_ht || 0), 0) || 0;
                    const discountAmt = createdQuote.discount_value > 0
                      ? (createdQuote.discount_type === 'percent' ? subtotalHT * createdQuote.discount_value / 100 : createdQuote.discount_value)
                      : 0;
                    return (
                      <>
                        {discountAmt > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Sous-total HT</span>
                            <span>{formatCurrency(subtotalHT)}</span>
                          </div>
                        )}
                        {discountAmt > 0 && (
                          <div className="flex justify-between text-sm text-red-500">
                            <span>Remise ({createdQuote.discount_type === 'percent' ? `${createdQuote.discount_value}%` : 'fixe'})</span>
                            <span>-{formatCurrency(discountAmt)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{discountAmt > 0 ? 'Net HT' : 'Total HT'}</span>
                          <span>{formatCurrency(createdQuote.total_ht)}</span>
                        </div>
                        {(() => {
                          const tvaMap = {};
                          createdQuote.lines?.forEach(l => {
                            if (!tvaMap[l.tva_rate]) tvaMap[l.tva_rate] = 0;
                            tvaMap[l.tva_rate] += (l.total_ht || 0) * (l.tva_rate / 100);
                          });
                          const entries = Object.entries(tvaMap).sort(([a],[b]) => parseFloat(a)-parseFloat(b));
                          return (<>
                            {discountAmt === 0 && entries.map(([rate, amount]) => (
                              <div key={rate} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">TVA {rate}%</span>
                                <span>{formatCurrency(amount)}</span>
                              </div>
                            ))}
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{discountAmt === 0 && entries.length > 1 ? 'Total TVA' : 'TVA'}</span>
                              <span>{formatCurrency(createdQuote.total_tva)}</span>
                            </div>
                          </>);
                        })()}
                        <div className="flex justify-between items-center pt-2 border-t border-white/10">
                          <span className="font-bold text-lg">Total TTC</span>
                          <span className="text-2xl font-bold text-primary">{formatCurrency(createdQuote.total_ttc)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground text-center">Que souhaitez-vous faire maintenant ?</p>
                
                <Button
                  onClick={handleSignNow}
                  className="w-full h-12 bg-primary text-primary-foreground hover:bg-accent font-bold rounded-full"
                  data-testid="sign-now-btn"
                >
                  <PenLine className="w-5 h-5 mr-2" />
                  Faire signer maintenant
                </Button>
                
                <Button
  onClick={handleSendEmail}
  disabled={sendingEmail}
  variant="outline"
  className="w-full h-12 border-primary text-primary hover:bg-primary/10 font-bold rounded-full"
  data-testid="send-email-btn"
>
  <Send className="w-5 h-5 mr-2" />
  {sendingEmail ? 'Envoi en cours...' : 'Envoyer par email'}
</Button>
                
                {!createdQuote.client?.email && (
                  <p className="text-xs text-amber-500 text-center">
                    ⚠️ Email du client non renseigné
                  </p>
                )}

                <Button
                  onClick={handleViewQuote}
                  variant="ghost"
                  className="w-full text-muted-foreground hover:text-foreground"
                  data-testid="view-quote-btn"
                >
                  Voir le devis détaillé
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteBuilderPage;
